import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const EXTERNAL_CLUSTER_CREDENTIALS_QUERY_KEY = 'external-cluster-credentials';

const getCredentialsScriptQueryKey = (params: CredentialsScriptParams) =>
  [
    EXTERNAL_CLUSTER_CREDENTIALS_QUERY_KEY,
    params.id,
    params.installGPUPlugin,
    params.installSecurityAgent,
    params.installAutoscalerAgent,
    params.installGpuMetricsExporter,
    params.installNetflowExporter,
    params.installAiOptimizerProxy,
    params.installWorkloadAutoscaler,
    params.installOmni,
    params.installPodMutator,
  ] as const;

type CredentialsScriptParams = {
  id?: string;
  installGPUPlugin?: boolean;
  installSecurityAgent?: boolean;
  installAutoscalerAgent?: boolean;
  installGpuMetricsExporter?: boolean;
  installNetflowExporter?: boolean;
  installAiOptimizerProxy?: boolean;
  installWorkloadAutoscaler?: boolean;
  installOmni?: boolean;
  installPodMutator?: boolean;
  enabled?: boolean;
};

const getCredentialsScriptQueryOptions = ({
  id,
  installGPUPlugin,
  installSecurityAgent,
  installAutoscalerAgent,
  installGpuMetricsExporter,
  installNetflowExporter,
  installAiOptimizerProxy,
  installWorkloadAutoscaler,
  installOmni,
  installPodMutator,
  enabled = true,
}: CredentialsScriptParams) =>
  queryOptions({
    queryKey: getCredentialsScriptQueryKey({
      id,
      installGPUPlugin,
      installSecurityAgent,
      installAutoscalerAgent,
      installGpuMetricsExporter,
      installNetflowExporter,
      installAiOptimizerProxy,
      installWorkloadAutoscaler,
      installOmni,
      installPodMutator,
    }),
    queryFn: async () => {
      const { data } = await apiClient.clusters.getCredentialsScript({
        clusterId: id!,
        crossRole: true,
        nvidiaDevicePlugin: installGPUPlugin,
        installSecurityAgent,
        installAutoscalerAgent,
        installGpuMetricsExporter,
        installNetflowExporter,
        installAiOptimizerProxy,
        installWorkloadAutoscaler,
        installOmni,
        installPodMutator,
      });

      return data.script;
    },
    refetchOnWindowFocus: false,
    enabled: makeIsEnabled(!!id, enabled),
  });

export const useCredentialsScriptQuery = ({
  id,
  installGPUPlugin,
  installSecurityAgent,
  installAutoscalerAgent,
  installGpuMetricsExporter,
  installNetflowExporter,
  installAiOptimizerProxy,
  installWorkloadAutoscaler,
  installOmni,
  installPodMutator = true,
  enabled = true,
}: CredentialsScriptParams & { enabled?: boolean }) => {
  const { isLoading, isFetching, data, error, refetch } = useQuery(
    getCredentialsScriptQueryOptions({
      id,
      installGPUPlugin,
      installSecurityAgent,
      installAutoscalerAgent,
      installGpuMetricsExporter,
      installNetflowExporter,
      installAiOptimizerProxy,
      installWorkloadAutoscaler,
      installOmni,
      installPodMutator,
      enabled,
    })
  );

  return {
    isLoading,
    isFetching,
    script: data,
    error,
    refetch: () => refetch({ cancelRefetch: true }),
  };
};
